import {
  Badge,
  Group,
  Image,
  Loader,
  Paper,
  Radio,
  ScrollArea,
  Stack,
  Text,
} from '@mantine/core';
import type { RequestableGroupProjection } from '@/lib/serviceRequestOriginatorService';
import { formatUsTimeZoneAbbreviation } from '@/lib/usTimeZoneLabel';

interface RequestableGroupSelectorProps {
  groups: RequestableGroupProjection[];
  value: string | 'help-find' | null;
  onChange: (value: string | 'help-find') => void;
  loading?: boolean;
  disabled?: boolean;
  error?: boolean;
}

const AVAILABILITY = {
  accepting: { label: 'Accepting', color: 'green' },
  waitlist: { label: 'Waitlist', color: 'yellow' },
  unavailable: { label: 'Unavailable', color: 'gray' },
} as const;

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function RequestableGroupSelector({
  groups,
  value,
  onChange,
  loading = false,
  disabled = false,
  error = false,
}: RequestableGroupSelectorProps): JSX.Element {
  if (loading) {
    return (
      <Group justify="center" py="lg">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Loading Groups…
        </Text>
      </Group>
    );
  }

  return (
    <Radio.Group
      value={value ?? ''}
      onChange={onChange}
      aria-label="Choose a Group"
      aria-invalid={error}
    >
      <Paper
        withBorder
        radius="md"
        p="xs"
        style={{ borderColor: 'var(--mantine-color-black)' }}
      >
        <ScrollArea.Autosize mah={360} offsetScrollbars>
          <Stack gap="xs" pr="xs">
            <Radio.Card
              value="help-find"
              disabled={disabled}
              p="md"
              radius="md"
              withBorder
            >
              <Group wrap="nowrap" align="center">
                <Radio.Indicator />
                <div>
                  <Text fw={600}>Help me find the right Group</Text>
                  <Text size="sm" c="dimmed">
                    Our team will match you based on your needs and
                    availability.
                  </Text>
                </div>
              </Group>
            </Radio.Card>

            {groups.map((group) => {
              const availability = AVAILABILITY[group.availability];
              const unavailable = group.availability === 'unavailable';
              return (
                <Radio.Card
                  key={group.groupId}
                  value={group.groupId}
                  disabled={disabled || unavailable}
                  p="md"
                  radius="md"
                  withBorder
                  aria-label={`${group.name}, ${availability.label}`}
                >
                  <Group wrap="nowrap" align="center">
                    <Radio.Indicator />
                    {group.imageUrl ? (
                      <Image
                        src={group.imageUrl}
                        alt=""
                        w={72}
                        h={72}
                        radius="md"
                        fit="cover"
                      />
                    ) : null}
                    <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                      <Group
                        justify="space-between"
                        align="flex-start"
                        wrap="nowrap"
                      >
                        <Text fw={600}>{group.name}</Text>
                        <Badge
                          color={availability.color}
                          variant="light"
                          size="sm"
                        >
                          {availability.label}
                        </Badge>
                      </Group>
                      {group.scheduleSummary || group.timezone ? (
                        <Text size="sm" c="gray.7">
                          {[
                            group.scheduleSummary,
                            group.timezone
                              ? formatUsTimeZoneAbbreviation(group.timezone)
                              : undefined,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </Text>
                      ) : null}
                      {group.modality ? (
                        <Text size="sm" c="dimmed">
                          {capitalize(group.modality)}
                        </Text>
                      ) : null}
                      {group.description ? (
                        <Text size="sm" c="dimmed" lineClamp={2}>
                          {group.description}
                        </Text>
                      ) : null}
                    </Stack>
                  </Group>
                </Radio.Card>
              );
            })}

            {groups.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="md">
                No specific Groups are currently listed. Choose “Help me find
                the right Group.”
              </Text>
            ) : null}
          </Stack>
        </ScrollArea.Autosize>
      </Paper>
    </Radio.Group>
  );
}
